// src/pages/commercial/CommercialCommissionsPage.js
import React, { useEffect, useState } from 'react';
import CommercialLayout from '../../components/commercial/CommercialLayout';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbase';

export default function CommercialCommissionsPage() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [quotes, setQuotes] = useState([]);

    // Config
    const commissionRate = 25; // Fixed 25%
    const [monthlyGoal, setMonthlyGoal] = useState(50000); // 50k CA Goal? or Commission Goal? Let's say Commission Goal.

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                // Fetch ALL quotes (we filter client-side for flexibility or simple date ranges later)
                const res = await pb.collection('quotes').getFullList({
                    sort: '-created',
                    expand: 'customer_id',
                    filter: `customer_id.commercial_id = "${user.id}"`
                });
                setQuotes(res);
            } catch (err) {
                console.error("Error fetching quotes", err);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [user.id]);

    // Calculations
    const signedQuotes = quotes.filter(q => q.status === 'accepted' || q.status === 'won'); // 'won' just in case legacy
    const pipelineQuotes = quotes.filter(q => ['sent', 'draft'].includes(q.status));

    const totalSales = signedQuotes.reduce((acc, q) => acc + (q.total || 0), 0);
    const pipelineValue = pipelineQuotes.reduce((acc, q) => acc + (q.total || 0), 0);

    const commissionEarned = totalSales * (commissionRate / 100);
    const commissionPotential = pipelineValue * (commissionRate / 100);

    // Goal Progress (based on earnings)
    const progressPercent = Math.min(100, Math.round((commissionEarned / monthlyGoal) * 100));

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
    };

    return (
        <CommercialLayout title="Mes Rémunérations 💰">
            <div className="commercial-page-wrapper">
                <style>{`
                .commercial-page-wrapper {
                    --admin-panel: var(--commercial-panel, #fff);
                    --admin-panel-2: var(--commercial-panel-2, #f9fafb);
                    --admin-border: var(--commercial-border, #e2e8f0);
                    --admin-text: var(--commercial-text, #0f172a);
                    --admin-muted: var(--commercial-muted, #64748b);
                    --admin-accent: var(--commercial-accent, #4f46e5);
                    --chart-green: #10b981;
                    --chart-blue: #3b82f6;
                    --chart-purple: #8b5cf6;
                }

                .commercial-page-wrapper .simulator-card {
                    background: var(--admin-panel);
                    border: 1px solid var(--admin-border);
                    border-radius: 16px;
                    padding: 24px;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
                }

                .commercial-page-wrapper .kpi-grid {
                    display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 24px; margin-bottom: 32px;
                }

                .commercial-page-wrapper .info-banner {
                    background: rgba(79, 70, 229, 0.1); border: 1px solid rgba(79, 70, 229, 0.2);
                    color: var(--admin-accent); padding: 16px; border-radius: 12px; margin-bottom: 24px;
                    display: flex; align-items: center; gap: 12px; font-weight: 500;
                }

                .commercial-page-wrapper .progress-bar {
                    height: 12px; background: var(--admin-border); border-radius: 6px; overflow: hidden; margin-top: 8px;
                }
                .commercial-page-wrapper .progress-fill {
                    height: 100%; background: linear-gradient(90deg, var(--chart-blue), var(--chart-purple));
                    transition: width 0.5s ease-out;
                }

                .commercial-page-wrapper .deal-list {
                    display: flex; flex-direction: column; gap: 12px;
                }
                .commercial-page-wrapper .deal-item {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 12px 16px; border-radius: 10px;
                    background: var(--admin-panel-2);
                    border: 1px solid transparent;
                }
                .commercial-page-wrapper .tag {
                    padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; text-transform: uppercase;
                }
                .commercial-page-wrapper .tag-won { background: rgba(16, 185, 129, 0.1); color: var(--chart-green); }
                .commercial-page-wrapper .tag-open { background: rgba(59, 130, 246, 0.1); color: var(--chart-blue); }

                `}</style>

                <div className="info-banner">
                    <span style={{ fontSize: '1.2rem' }}>ℹ️</span>
                    Votre rémunération est calculée sur la base de <strong>{commissionRate}%</strong> du Chiffre d'Affaires HT validé.
                </div>

                {/* KPI Cards */}
                <div className="kpi-grid">
                    <div className="simulator-card" style={{ borderLeft: '4px solid var(--chart-green)' }}>
                        <span style={{ fontSize: '0.9rem', color: 'var(--admin-muted)' }}>Rémunération Net (Gagnée)</span>
                        <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--chart-green)', margin: '8px 0' }}>
                            {formatCurrency(commissionEarned)}
                        </div>
                        <span style={{ fontSize: '0.9rem', fontWeight: 500, display: 'block', color: 'var(--admin-text)' }}>
                            Chiffre d'Affaires: {formatCurrency(totalSales)}
                        </span>
                    </div>

                    <div className="simulator-card" style={{ borderLeft: '4px solid var(--chart-blue)' }}>
                        <span style={{ fontSize: '0.9rem', color: 'var(--admin-muted)' }}>Potentiel (Affaires en cours)</span>
                        <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--chart-blue)', margin: '8px 0' }}>
                            {formatCurrency(commissionPotential)}
                        </div>
                        <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                            Si tous les devis en cours sont signés
                        </span>
                    </div>

                    <div className="simulator-card" style={{ borderLeft: '4px solid var(--chart-purple)' }}>
                        <span style={{ fontSize: '0.9rem', color: 'var(--admin-muted)' }}>Objectif Gains</span>
                        <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--chart-purple)', margin: '8px 0' }}>
                            {progressPercent}%
                        </div>
                        <div className="progress-bar">
                            <div className="progress-fill" style={{ width: `${progressPercent}%` }}></div>
                        </div>
                        <div style={{ marginTop: 8, fontSize: '0.85rem', color: 'var(--admin-muted)' }}>
                            {formatCurrency(commissionEarned)} / {formatCurrency(monthlyGoal)} (Objectif)
                        </div>
                    </div>
                </div>

                {/* Details Section */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
                    <div className="simulator-card">
                        <h3 style={{ marginTop: 0 }}>Dernières Ventes (Validées)</h3>
                        <div className="deal-list">
                            {signedQuotes.length === 0 ? (
                                <p style={{ color: 'var(--admin-muted)', fontStyle: 'italic' }}>Aucune vente validée pour le moment.</p>
                            ) : (
                                signedQuotes.slice(0, 5).map(q => (
                                    <div key={q.id} className="deal-item">
                                        <div>
                                            <div style={{ fontWeight: 600 }}>{q.reference}</div>
                                            <div style={{ fontSize: '0.85rem', color: 'var(--admin-muted)' }}>
                                                {q.expand?.customer_id?.name || 'Client Inconnu'}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontWeight: 700, color: 'var(--chart-green)' }}>
                                                +{formatCurrency(q.total * (commissionRate / 100))}
                                            </div>
                                            <div style={{ fontSize: '0.8rem', color: 'var(--admin-muted)' }}>
                                                CA: {formatCurrency(q.total)}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    <div className="simulator-card">
                        <h3 style={{ marginTop: 0 }}>En Attente (Affaires en cours)</h3>
                        <div className="deal-list">
                            {pipelineQuotes.length === 0 ? (
                                <p style={{ color: 'var(--admin-muted)', fontStyle: 'italic' }}>Aucune affaire en cours.</p>
                            ) : (
                                pipelineQuotes.slice(0, 5).map(q => (
                                    <div key={q.id} className="deal-item">
                                        <div>
                                            <div style={{ fontWeight: 600 }}>{q.reference}</div>
                                            <div className="tag tag-open" style={{ display: 'inline-block', marginTop: 4 }}>
                                                {q.status === 'sent' ? 'Envoyé' : 'Brouillon'}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontWeight: 700, color: 'var(--chart-blue)' }}>
                                                ~ {formatCurrency(q.total * (commissionRate / 100))}
                                            </div>
                                            <div style={{ fontSize: '0.8rem', color: 'var(--admin-muted)' }}>
                                                CA: {formatCurrency(q.total)}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

            </div>
        </CommercialLayout>
    );
}
